// Words that should keep specific casing regardless of context.
// Keys are lowercase tokens detected after splitting an identifier; values are the canonical display form.
const ACRONYMS: Record<string, string> = {
  ai: 'AI',
  id: 'ID',
  ids: 'IDs',
  qr: 'QR',
  pdf: 'PDF',
  pdfs: 'PDFs',
  ocr: 'OCR',
  json: 'JSON',
  url: 'URL',
  urls: 'URLs',
  bsn: 'BSN',
  pii: 'PII',
  llm: 'LLM',
  llms: 'LLMs',
  http: 'HTTP',
  https: 'HTTPS',
  api: 'API',
  apis: 'APIs',
  csv: 'CSV',
  xml: 'XML',
  yaml: 'YAML',
  uuid: 'UUID',
  uri: 'URI',
  mcp: 'MCP',
};

/**
 * Split a camelCase / PascalCase / snake_case / kebab-case identifier into tokens.
 *
 * Examples:
 *  "FileIds"      -> ["File", "Ids"]
 *  "AiSettings"   -> ["Ai", "Settings"]
 *  "TextLayerJsonId" -> ["Text", "Layer", "Json", "Id"]
 *  "convertToPdf" -> ["convert", "To", "Pdf"]
 */
export const splitTokens = (raw: string): string[] => {
  if (!raw) return [];
  // Split on non-alphanumeric separators first, then on camelCase boundaries.
  const cleaned = raw.replace(/[_\-.]+/g, ' ').trim();
  const camelSplit = cleaned
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2'); // ABCWord -> ABC Word
  return camelSplit.split(/\s+/).filter(Boolean);
};

/**
 * Format an identifier into a human-friendly label.
 * - "AiSettings"       -> "AI Settings"
 * - "FileIds"          -> "File IDs"
 * - "FileId"           -> "File ID"
 * - "QrCode"           -> "QR Code"
 * - "TextLayerJsonId"  -> "Text Layer JSON ID"
 * - "ExcludeAttachments" -> "Exclude Attachments"
 */
export const formatIdentifier = (raw: string): string => {
  const tokens = splitTokens(raw);
  if (tokens.length === 0) return raw;

  return tokens
    .map((token, idx) => {
      const lower = token.toLowerCase();
      if (ACRONYMS[lower]) return ACRONYMS[lower];
      // First token: keep capitalization; later tokens: capitalize first letter, lowercase rest.
      if (idx === 0) {
        return token.charAt(0).toUpperCase() + token.slice(1);
      }

      return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    })
    .join(' ');
};

/**
 * Format a label according to the user-selected naming mode.
 * "formatted" → human-friendly; "raw" → original identifier untouched.
 */
export const formatName = (raw: string, mode: 'formatted' | 'raw' = 'formatted'): string => {
  if (mode === 'raw') return raw;
  return formatIdentifier(raw);
};
